// Seed songs — loaded into localStorage on first run.
// Edit or delete freely; they're just initial content.

import type { Song } from './types';

export const DEFAULT_SONGS: Song[] = [
  {
    id: 'daft-punk-around-the-world',
    title: 'Around the World (sketch)',
    code: `// ============================================================
// "Around the World" — loose homage
// ------------------------------------------------------------
// Voices -> Sections -> Arrangement.
//   stack(...)          layers patterns (play together)
//   arrange([n, p], …)  sequences patterns (play for n cycles)
// ============================================================

setcpm(123 / 4) // ~123 BPM

// ---- voices ----
const kick = s("bd*4").gain(0.95)
const hats = s("- hh - hh").gain(0.35).speed(1.1)
const ohat = s("- - - ho").gain(0.3)
const clap = s("- cp").gain(0.55)

// 13-note bassline in D minor (real track is B minor)
const bass = note("d2 d2 d2 d2 a2 d3 c3 d3 f3 a2 d3 c3 a2 c3 d3")
  .s("sawtooth")
  .lpf(sine.range(500, 2400).slow(8))
  .lpq(9)
  .attack(0.005).release(0.12)
  .gain(0.78)

const stabs = note("<[d3,f3,a3,c4] [f3,a3,c4,e4] [a3,c4,e4,g4] [g3,bb3,d4,f4]>")
  .struct("t ~ ~ t ~ ~ t ~")
  .s("supersaw")
  .attack(0.01).release(0.25)
  .lpf(sine.range(600, 3200).slow(16))
  .gain(0.5)

const lead = note("a4 ~ a4 g4 ~ f4 e4 d4 ~ f4 a4 ~").slow(2)
  .s("square")
  .lpf(2000)
  .delay(0.35).delaytime(0.375).delayfb(0.45)
  .gain(0.42)

// Explicit chord voicings — one bracketed stack per chord. Safer than
// piping chord symbols through tonal, which interprets the pattern as
// scale steps when a scale() is chained.
//
//   Dm7  = D F A C
//   F    = F A C
//   Am7  = A C E G
//   Gm7  = G Bb D F
const pad = note("<[d3,f3,a3,c4] [f3,a3,c4] [a3,c4,e4,g4] [g3,bb3,d4,f4]>")
  .s("supersaw")
  .attack(0.8).release(1.5)
  .lpf(900)
  .gain(0.25)

// ---- sections ----
const drums     = stack(kick, hats, ohat)
const intro     = stack(kick, hats)
const verse     = stack(drums, bass)
const build     = stack(drums, bass, pad)
const chorus    = stack(drums, bass, stabs, lead, clap)
const breakdown = stack(bass.lpf(250), pad)

// ---- arrangement ----
arrange(
  [4,  intro],
  [8,  verse],
  [8,  build],
  [16, chorus],
  [4,  breakdown],
  [16, chorus],
)
`,
  },
  {
    id: 'daft-punk-da-funk',
    title: 'Da Funk (acid bass sketch)',
    code: `// ============================================================
// "Da Funk" vibe — TB-303-style acid bass
// Single-section piece — just one stack, no arrange().
// Start here if you want to learn Strudel without song structure.
// ============================================================

setcpm(110 / 4) // ~110 BPM

const kick  = s("bd:2*4").gain(0.9)
const snare = s("- cp - cp").gain(0.5)
const hats  = s("hh*8").gain(0.3).speed(1.2)

// The acid sound is 90% filter + resonance automation.
// Try pushing .lpq up past 25 for proper squelch.
const acid = note("c2 ~ c2 eb2 c2 ~ g2 c2 ~ c2 eb2 g2 f2 c2 ~ g1")
  .slow(2)
  .s("sawtooth")
  .lpf(sine.range(150, 2800).slow(16))
  .lpq(perlin.range(10, 22).slow(4))
  .attack(0.001).release(0.12)
  .gain(0.8)

stack(kick, snare, hats, acid)
`,
  },
  {
    id: 'template-house',
    title: 'House (template)',
    code: `// ============================================================
// HOUSE · 124 BPM
// ------------------------------------------------------------
// The rhythmic signature: four-on-the-floor kick, open hat on
// every offbeat (the "chk chk chk chk" that drives the groove),
// clap reinforcing beats 2 and 4. Bass tucks under the kick.
// ============================================================

setcpm(124 / 4)

const kick  = s("bd*4").gain(0.95)
const ohat  = s("- ho - ho - ho - ho").gain(0.32)
const chat  = s("hh*8").gain(0.18).speed(1.1)
const clap  = s("- cp - cp").gain(0.6)

// Bass on downbeats + a pickup — classic house root walk.
const bass = note("<a1 a1 f1 g1>")
  .struct("t ~ t ~ t ~ t t")
  .s("sawtooth")
  .lpf(sine.range(400, 1400).slow(4))
  .lpq(6)
  .attack(0.005).release(0.12)
  .gain(0.7)

// Plucky stab on the upbeat of beats 2 and 4.
const stab = note("<[a3,c4,e4] [f3,a3,c4] [g3,bb3,d4] [a3,c4,e4]>")
  .struct("~ ~ t ~ ~ ~ t ~")
  .s("square")
  .attack(0.005).release(0.18)
  .lpf(2200)
  .gain(0.38)

stack(kick, ohat, chat, clap, bass, stab)
`,
  },
  {
    id: 'template-dnb',
    title: 'Drum & Bass (template)',
    code: `// ============================================================
// DRUM & BASS · 172 BPM
// ------------------------------------------------------------
// Half-time feel from the drums — snare on beat 3, kick on 1
// and on a syncopated pickup before 3. 16th-note ride throughout.
// Sub bass follows the snare, leaving space on the drops.
// ============================================================

setcpm(172 / 4)

const kick  = s("bd ~ ~ ~ ~ ~ bd ~").gain(0.95)
const snare = s("~ ~ ~ ~ sd ~ ~ ~").gain(0.7)
// Classic amen-ish ghost hits between the backbeat.
const ghost = s("~ ~ sd:1 ~ ~ ~ ~ sd:1").gain(0.25)
const ride  = s("hh*16").gain(0.22).speed(1.2)

const sub = note("<a1 a1 c2 g1>")
  .struct("t ~ ~ ~ t t ~ ~")
  .s("sine")
  .attack(0.002).release(0.22)
  .gain(0.9)

// Sparse stab — one chord per two cycles, filter sweep.
const chord = note("<[a3,c4,e4] [c4,e4,g4]>")
  .struct("t ~ ~ ~ ~ ~ ~ ~")
  .slow(2)
  .s("supersaw")
  .attack(0.03).release(0.35)
  .lpf(sine.range(600, 2800).slow(8))
  .gain(0.3)

stack(kick, snare, ghost, ride, sub, chord)
`,
  },
  {
    id: 'template-techno',
    title: 'Techno (template)',
    code: `// ============================================================
// TECHNO · 128 BPM
// ------------------------------------------------------------
// Minimal and hypnotic. Four-on-the-floor, offbeat shaker,
// sparse stabs on a long cycle, acid bass with slow filter
// envelope moving under everything.
// ============================================================

setcpm(128 / 4)

const kick = s("bd*4").gain(0.98)
// Offbeat shaker / closed hat — the "tss" between kicks.
const shkr = s("- hh:2 - hh:2").gain(0.26).speed(1.4)
// Sparse ride ticking a 16th on the last quarter.
const ride = s("~ ~ ~ ~ ~ ~ hh:5 ~").gain(0.15).speed(0.9)

// Acid bass — key to the genre. Slow LFO opens the filter
// over 16 cycles; resonance sits high for squelch.
const acid = note("a1 ~ a1 eb2 ~ a1 g1 ~ a1 ~ a1 c2 ~ a1 g1 f1")
  .s("sawtooth")
  .lpf(sine.range(200, 2600).slow(16))
  .lpq(18)
  .attack(0.001).release(0.1)
  .gain(0.85)

// Sparse chord stab — one per 4 cycles, evolves.
const stab = note("<[a3,c4,e4] [a3,d4,f4] [a3,c4,f4] [a3,eb4,g4]>")
  .struct("~ ~ ~ ~ ~ ~ ~ t")
  .slow(2)
  .s("square")
  .attack(0.005).release(0.22)
  .lpf(1800)
  .delay(0.3).delaytime(0.188).delayfb(0.5)
  .gain(0.35)

stack(kick, shkr, ride, acid, stab)
`,
  },
];
